import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface Member {
  id: number;
  organization_id: number;
  profile_id: number;
  role: string;
  email: string;
  full_name?: string | null;
  invited_by_profile_id?: number | null;
  invited_at?: string | null;
  joined_at?: string | null;
  is_pending?: boolean;
  invitation_expires_at?: string | null;
  invitation_id?: number;
  invitation_token?: string | null;
  status: string;
}

interface MemberRowProps {
  member: Member;
  canManage: boolean;
  currentUserId?: string;
  onRemove: (memberId: number) => void;
  onChangeRole: (memberId: number, newRole: string) => void;
  onRevoke: (invitationId: number) => void;
  onResend: (invitationId: number) => void;
}

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-red-100 text-red-700 border-red-200',
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  member: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700 border-green-200',
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
};

function getTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  return `${hours}h left`;
}

export function MemberRow({
  member,
  canManage,
  currentUserId,
  onRemove,
  onChangeRole,
  onRevoke,
  onResend,
}: MemberRowProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const isSelf = currentUserId ? String(member.profile_id) === currentUserId : false;
  const isOwner = member.role === 'owner';
  const isPending = member.status === 'pending';
  const isExpired = member.status === 'expired' || (
    member.invitation_expires_at && new Date(member.invitation_expires_at) < new Date()
  );

  return (
    <>
      <tr className="border-b border-border hover:bg-muted/50">
        <td className="py-3 px-4">
          <div className="font-medium">
            {member.full_name ?? member.email ?? '—'}
          </div>
          {member.full_name && (
            <div className="text-xs text-muted-foreground">{member.email}</div>
          )}
        </td>
        <td className="py-3 px-4">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_COLORS[member.role] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {member.role}
          </span>
        </td>
        <td className="py-3 px-4">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[member.status] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {isPending && <i className="bi bi-hourglass-split text-[10px]"></i>}
            {member.status}
          </span>
          {isPending && member.invitation_expires_at && (
            <div className="text-xs text-muted-foreground mt-1">
              {getTimeRemaining(member.invitation_expires_at)}
            </div>
          )}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-1">
            {canManage && !isOwner && !isSelf && (
              <>
                <Select
                  value={member.role}
                  onValueChange={(v) => { if (v) onChangeRole(member.id, v) }}
                >
                  <SelectTrigger size="sm" className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRemove(true)}
                >
                  <i className="bi bi-person-x text-sm text-red-600"></i>
                </Button>
              </>
            )}
            {canManage && isPending && member.invitation_id && (
              <>
                {member.invitation_token && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const url = `${window.location.origin}/invite/${member.invitation_token}`;
                      void navigator.clipboard.writeText(url);
                    }}
                    title="Copy invite link"
                  >
                    <i className="bi bi-link-45deg text-sm text-blue-600"></i>
                    <span className="text-xs ml-1">Copy Link</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRevoke(true)}
                >
                  <i className="bi bi-x-circle text-sm text-red-600"></i>
                  <span className="text-xs ml-1">Revoke</span>
                </Button>
              </>
            )}
            {canManage && isExpired && member.invitation_id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onResend(member.invitation_id!)}
              >
                <i className="bi bi-arrow-repeat text-sm text-blue-600"></i>
                <span className="text-xs ml-1">Resend</span>
              </Button>
            )}
            {!canManage && isSelf && (
              <span className="text-xs text-muted-foreground">You</span>
            )}
          </div>
        </td>
      </tr>

      <Dialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {member.full_name ?? member.email} from the organization? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onRemove(member.id);
                setConfirmRemove(false);
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Invitation</DialogTitle>
            <DialogDescription>
              Revoke the pending invitation for {member.email}? They will not be able to accept this invitation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (member.invitation_id) onRevoke(member.invitation_id);
                setConfirmRevoke(false);
              }}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
